import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useHolidays } from '../../hooks/useHolidays';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, Bell, 
  Share2, Trash2, Edit, User, X, Check, Loader2, Paperclip, FileText, Image, Download, Eye,
  List, Pencil, AlertTriangle, Users
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import InfoBanner from '../../components/ui/info-banner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchCalendar = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [usersForShare, setUsersForShare] = useState([]);
  const [shareBranchName, setShareBranchName] = useState(null);
  
  // Hatırlatıcı states
  const [reminders, setReminders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showReminderListModal, setShowReminderListModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'normal',
    employee_id: '',
    type: 'general'
  });
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const { getHolidayForDate } = useHolidays(currentDate);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Modal states
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  
  // Form state
  const [noteForm, setNoteForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    alarm_enabled: false,
    alarm_time: '',
    share_with_users: [],
    scheduled_share_time: '',
    color: 'blue',
    attachments: []
  });
  
  // File upload state
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Preview modal state
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // YENİ Badge için state - okunmamış notları user.id'ye göre kontrol et
  // Not: Paylaşılan notlarda read_by array'inde user.id yoksa okunmamış demek

  const colorOptions = [
    { value: 'blue', label: 'Mavi', class: 'bg-blue-500' },
    { value: 'green', label: 'Yeşil', class: 'bg-green-500' },
    { value: 'red', label: 'Kırmızı', class: 'bg-red-500' },
    { value: 'yellow', label: 'Sarı', class: 'bg-yellow-500' },
    { value: 'purple', label: 'Mor', class: 'bg-purple-500' },
    { value: 'orange', label: 'Turuncu', class: 'bg-orange-500' },
    { value: 'pink', label: 'Pembe', class: 'bg-pink-500' },
    { value: 'cyan', label: 'Camgöbeği', class: 'bg-cyan-500' },
  ];

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  useEffect(() => {
    loadNotes();
    loadUsersForShare();
    loadReminders();
    loadEmployees();
  }, [currentDate]);

  // Hatırlatıcıları yükle
  const loadReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/branch/reminders`, {
        headers: getAuthHeaders()
      });
      setReminders(response.data || []);
    } catch (error) {
      console.error('Error loading reminders:', error);
      setReminders([]);
    }
  }, []);

  // Çalışanları yükle
  const loadEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/branch/employees`, {
        headers: getAuthHeaders()
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, []);

  // Not okunmamış mı kontrol et - paylaşılan notlar için read_by array'inde user.id olup olmadığına bak
  const isNoteUnread = (note) => {
    if (!note.is_shared) return false; // Kendi notları yeni değil
    if (!user?.id) return false;
    return !note.read_by?.includes(user.id);
  };

  // YENİ Badge - Not okundu olarak işaretle (mark-read API)
  const markNoteAsRead = async (noteId) => {
    try {
      await axios.post(`${API}/calendar/notes/${noteId}/mark-read`, {}, { headers: getAuthHeaders() });
      
      // State'i güncelle - notu okundu olarak işaretle
      setNotes(prev => prev.map(n => 
        n.id === noteId 
          ? { ...n, read_by: [...(n.read_by || []), user.id] }
          : n
      ));
      
      // Sidebar sayacını güncelle
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Not okundu yapılırken hata:', error);
    }
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const res = await axios.get(`${API}/calendar/notes`, {
        params: { year, month },
        headers: getAuthHeaders()
      });
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error('Notlar yüklenirken hata:', error);
      toast.error('Notlar yüklenemedi');
    }
    setLoading(false);
  };

  const loadUsersForShare = async () => {
    try {
      const res = await axios.get(`${API}/calendar/users-for-share`, {
        headers: getAuthHeaders()
      });
      setUsersForShare(res.data.users || []);
      setShareBranchName(res.data.branch_name || null);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const openNewNoteModal = (date = null) => {
    const dateStr = date ? formatDateToISO(date) : formatDateToISO(new Date());
    setNoteForm({
      title: '',
      description: '',
      date: dateStr,
      time: '',
      alarm_enabled: false,
      alarm_time: '',
      share_with_users: [],
      scheduled_share_time: '',
      color: 'blue',
      attachments: []
    });
    setEditingNote(null);
    setShowNoteModal(true);
  };

  const openEditModal = (note) => {
    setNoteForm({
      title: note.title || '',
      description: note.description || '',
      date: note.date || '',
      time: note.time || '',
      alarm_enabled: note.alarm_enabled || false,
      alarm_time: note.alarm_time || '',
      share_with_users: note.share_with_users || [],
      scheduled_share_time: note.scheduled_share_time || '',
      color: note.color || 'blue',
      attachments: note.attachments || []
    });
    setEditingNote(note);
    setShowNoteModal(true);
  };

  const openViewModal = (note) => {
    setViewingNote(note);
    setShowViewModal(true);
  };
  
  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/calendar/notes/upload-attachment`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNoteForm(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), res.data]
      }));
      
      toast.success('Belge yüklendi');
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      toast.error('Dosya yüklenemedi');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Remove attachment
  const removeAttachment = (attachmentId) => {
    setNoteForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== attachmentId)
    }));
  };
  
  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Download file
  const downloadFile = (attachment) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Preview file
  const openPreview = (attachment) => {
    setPreviewFile(attachment);
    setShowPreviewModal(true);
  };

  const formatDateToISO = (date) => {
    // Yerel saat dilimine göre tarih formatla (UTC dönüşümü ile 1 gün kaymasını önle)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    try {
      if (editingNote) {
        await axios.put(`${API}/calendar/notes/${editingNote.id}`, noteForm, {
          headers: getAuthHeaders()
        });
        toast.success('Not güncellendi');
      } else {
        await axios.post(`${API}/calendar/notes`, noteForm, {
          headers: getAuthHeaders()
        });
        toast.success('Not oluşturuldu');
      }
      setShowNoteModal(false);
      loadNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Bu notu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/calendar/notes/${noteId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Not silindi');
      setShowViewModal(false);
      loadNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const toggleUserShare = (userId) => {
    setNoteForm(prev => {
      const users = prev.share_with_users.includes(userId)
        ? prev.share_with_users.filter(id => id !== userId)
        : [...prev.share_with_users, userId];
      return { ...prev, share_with_users: users };
    });
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Pazartesi = 0
  };

  const getNotesForDate = (date) => {
    const dateStr = formatDateToISO(date);
    const dayNotes = notes.filter(n => n.date === dateStr);
    const dayReminders = reminders.filter(r => r.due_date === dateStr && !r.is_completed).map(r => ({
      ...r,
      is_reminder: true
    }));
    return [...dayNotes, ...dayReminders];
  };

  const getColorClass = (color) => {
    const opt = colorOptions.find(c => c.value === color);
    return opt ? opt.class : 'bg-blue-500';
  };

  const colorMap = {
    blue: '#3b82f6',
    green: '#22c55e',
    red: '#ef4444',
    yellow: '#eab308',
    purple: '#a855f7',
    orange: '#f97316',
    pink: '#ec4899',
    cyan: '#06b6d4',
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR');
  };

  // Hatırlatıcı helper functions
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'normal': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Yüksek';
      case 'normal': return 'Normal';
      case 'low': return 'Düşük';
      default: return priority;
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const isDueToday = (dueDate) => {
    const today = new Date().toDateString();
    return new Date(dueDate).toDateString() === today;
  };

  // Hatırlatıcı modalını aç
  const openReminderModal = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      setReminderForm({
        title: reminder.title || '',
        description: reminder.description || '',
        due_date: reminder.due_date || '',
        priority: reminder.priority || 'normal',
        employee_id: reminder.employee_id || '',
        type: reminder.type || 'general'
      });
    } else {
      setEditingReminder(null);
      setReminderForm({
        title: '',
        description: '',
        due_date: formatDateToISO(new Date()),
        priority: 'normal',
        employee_id: '',
        type: 'general'
      });
    }
    setShowReminderModal(true);
  };

  // Hatırlatıcı kaydet
  const handleSaveReminder = async () => {
    if (!reminderForm.title || !reminderForm.due_date) {
      toast.error('Başlık ve son tarih zorunludur');
      return;
    }

    try {
      if (editingReminder) {
        await axios.put(`${API}/branch/reminders/${editingReminder.id}`, reminderForm, {
          headers: getAuthHeaders()
        });
        toast.success('Hatırlatıcı güncellendi');
      } else {
        await axios.post(`${API}/branch/reminders`, reminderForm, {
          headers: getAuthHeaders()
        });
        toast.success('Hatırlatıcı oluşturuldu');
      }
      setShowReminderModal(false);
      loadReminders();
    } catch (error) {
      console.error('Error saving reminder:', error);
      toast.error('İşlem başarısız');
    }
  };

  // Hatırlatıcı tamamla
  const handleCompleteReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/branch/reminders/${reminderId}/complete`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Hatırlatıcı tamamlandı');
      loadReminders();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('İşlem başarısız');
    }
  };

  // Hatırlatıcı sil
  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/branch/reminders/${reminderId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Hatırlatıcı silindi');
      loadReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Silme işlemi başarısız');
    }
  };

  // Aktif ve tamamlanan hatırlatıcıları ayır
  const activeReminders = reminders.filter(r => !r.is_completed);
  const completedReminders = reminders.filter(r => r.is_completed);

  // Belirli bir tarih için hatırlatıcıları getir
  const getRemindersForDate = (date) => {
    const dateStr = formatDateToISO(date);
    return reminders.filter(r => r.due_date === dateStr && !r.is_completed);
  };

  // Get combined notes and reminders for date
  const getEventsForDate = (date) => {
    const dateStr = formatDateToISO(date);
    const dayNotes = notes.filter(n => n.date === dateStr);
    const dayReminders = reminders.filter(r => r.due_date === dateStr && !r.is_completed).map(r => ({
      ...r,
      is_reminder: true
    }));
    return [...dayNotes, ...dayReminders];
  };

  // Build calendar grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Empty cells for days before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 border border-gray-100 bg-gray-50"></div>);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getNotesForDate(date);
      const isToday = formatDateToISO(date) === formatDateToISO(new Date());
      const holiday = getHolidayForDate(date);
      
      days.push(
        <div
          key={day}
          className={`h-28 border p-1 cursor-pointer hover:bg-blue-50 transition-colors ${
            holiday ? 'bg-rose-50 border-rose-300' : isToday ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200'
          }`}
          onClick={() => openNewNoteModal(date)}
        >
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
            {day}
          </div>
          {holiday && (
            <div className="text-[10px] leading-tight font-medium px-1 py-0.5 rounded bg-rose-600 text-white mb-1 truncate" title={holiday.name} data-testid={`holiday-cell-${holiday.date}`}>
              {holiday.name}{holiday.day_type === 'half' ? ' (Yarım)' : ''}
            </div>
          )}
          <div className="space-y-0.5 overflow-y-auto max-h-20">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className={cn(
                  "text-xs px-1 py-0.5 rounded truncate cursor-pointer",
                  event.is_reminder 
                    ? "bg-orange-500 text-white"
                    : `text-white ${getColorClass(event.color)}`
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (event.is_reminder) {
                    setShowReminderListModal(true);
                  } else {
                    openViewModal(event);
                  }
                }}
                title={event.title}
              >
                {event.is_reminder ? (
                  <>
                    <Bell className="inline h-2.5 w-2.5 mr-1" />
                    {event.title}
                  </>
                ) : (
                  <>
                    {event.time && <span className="mr-1">{event.time}</span>}
                    {event.title}
                    {event.is_shared && <Share2 className="inline h-2.5 w-2.5 ml-1" />}
                  </>
                )}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} daha</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="space-y-6" data-testid="work-calendar">
      {/* Bilgilendirme Notu */}
      <InfoBanner id="branch-calendar-info" variant="info">
        <strong>Takvim:</strong> Takvimi kullanarak ajandanıza yeni notlar ekleyebilir, hatırlatıcı kurabilir ve bu notlara istediğiniz kişileri ekleyebilirsiniz. 
        <strong>Hatırlatıcı ekle</strong> ile daha karmaşık zaman aralıklarında hatırlatmalar yapabilirsiniz. 
        <strong>Hatırlatıcı listesinden</strong> bu hatırlatmalara ulaşabilirsiniz. 
        Sadece kendi oluşturduğunuz notları silebilirsiniz.
      </InfoBanner>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Takvim / Ajanda</h1>
            <p className="text-gray-500">Notlarınızı ve etkinliklerinizi yönetin</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowReminderListModal(true)}
            variant="outline"
            className="gap-2"
            data-testid="reminder-list-btn"
          >
            <List className="h-4 w-4" />
            Hatırlatıcı Listesi
            {activeReminders.length > 0 && (
              <Badge className="bg-orange-100 text-orange-700 ml-1">{activeReminders.length}</Badge>
            )}
          </Button>
          <Button
            onClick={() => openReminderModal()}
            className="gap-2 bg-orange-600 hover:bg-orange-700"
            data-testid="add-reminder-btn"
          >
            <Bell className="h-4 w-4" />
            Hatırlatıcı Ekle
          </Button>
          <Button onClick={() => openNewNoteModal()} className="gap-2" data-testid="new-note-btn">
            <Plus className="h-4 w-4" />
            Yeni Not
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Bugün
              </Button>
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="text-sm text-gray-500">
              {notes.length} not
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2 bg-gray-100">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {renderCalendar()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Notes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} - Tüm Notlar
          </CardTitle>
          <p className="text-sm text-gray-500">Bu aydaki tüm notlarınız ve paylaşılan notlar</p>
        </CardHeader>
        <CardContent>
          {notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Bu ayda henüz not bulunmuyor</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {notes.sort((a, b) => {
                const dateA = new Date(a.date + 'T' + (a.time || '00:00'));
                const dateB = new Date(b.date + 'T' + (b.time || '00:00'));
                return dateA - dateB;
              }).map((note) => {
                const isUnread = isNoteUnread(note);
                return (
                <div
                  key={note.id}
                  className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${
                    isUnread ? 'bg-blue-50 ring-2 ring-blue-300' : 
                    note.is_shared ? 'bg-purple-50 border-purple-400' : 'bg-white border-blue-400'
                  }`}
                  style={{ borderLeftColor: note.color ? colorMap[note.color] : undefined }}
                  onClick={() => {
                    if (isUnread) {
                      markNoteAsRead(note.id);
                    }
                    openViewModal(note);
                  }}
                  data-testid={`note-card-${note.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full`} style={{ backgroundColor: colorMap[note.color] || '#3b82f6' }}></span>
                        <h3 className="font-semibold text-gray-800 truncate">{note.title}</h3>
                        {isUnread && (
                          <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                        )}
                        {note.is_shared && (
                          <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                            <Share2 className="h-3 w-3 mr-1" />
                            Paylaşılan
                          </Badge>
                        )}
                      </div>
                      {note.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{note.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {new Date(note.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                        </span>
                        {note.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {note.time}
                          </span>
                        )}
                        {note.alarm_enabled && (
                          <Bell className="h-3 w-3 text-orange-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {note.is_shared ? `Paylaşan: ${note.user_name}` : `Oluşturan: ${note.user_name}`}
                      </p>
                    </div>
                    {!note.is_shared && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNote(note.id);
                        }}
                        title="Notu Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note Create/Edit Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Notu Düzenle' : 'Yeni Not Oluştur'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                placeholder="Not başlığı"
                data-testid="note-title"
              />
            </div>

            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={noteForm.description}
                onChange={(e) => setNoteForm({...noteForm, description: e.target.value})}
                placeholder="Detaylı açıklama"
                rows={3}
                data-testid="note-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={noteForm.date}
                  onChange={(e) => setNoteForm({...noteForm, date: e.target.value})}
                  data-testid="note-date"
                />
              </div>
              <div>
                <Label>Saat</Label>
                <Input
                  type="time"
                  value={noteForm.time}
                  onChange={(e) => setNoteForm({...noteForm, time: e.target.value})}
                  data-testid="note-time"
                />
              </div>
            </div>

            <div>
              <Label>Renk</Label>
              <div className="flex gap-2 mt-1">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full ${color.class} ${
                      noteForm.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                    }`}
                    onClick={() => setNoteForm({...noteForm, color: color.value})}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Alarm */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-orange-500" />
                  <Label>Alarm / Hatırlatıcı</Label>
                </div>
                <Switch
                  checked={noteForm.alarm_enabled}
                  onCheckedChange={(checked) => setNoteForm({...noteForm, alarm_enabled: checked})}
                />
              </div>
              {noteForm.alarm_enabled && (
                <Input
                  type="datetime-local"
                  value={noteForm.alarm_time}
                  onChange={(e) => setNoteForm({...noteForm, alarm_time: e.target.value})}
                  placeholder="Alarm zamanı"
                />
              )}
            </div>

            {/* Share */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-blue-500" />
                  <Label>Paylaşım</Label>
                </div>
                {shareBranchName && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {shareBranchName} şubesi
                  </span>
                )}
              </div>
              
              <div className="max-h-32 overflow-y-auto space-y-1">
                {usersForShare.map(u => (
                  <div
                    key={u.id}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                      noteForm.share_with_users.includes(u.id) ? 'bg-blue-100' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => toggleUserShare(u.id)}
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{u.name}</span>
                    </div>
                    {noteForm.share_with_users.includes(u.id) && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                ))}
                {usersForShare.length === 0 && (
                  <p className="text-sm text-gray-500">
                    {shareBranchName ? 'Bu şubede paylaşılacak kullanıcı yok' : 'Paylaşılacak kullanıcı yok'}
                  </p>
                )}
              </div>

              {noteForm.share_with_users.length > 0 && (
                <div>
                  <Label className="text-sm">Zamanlanmış Paylaşım (Opsiyonel)</Label>
                  <Input
                    type="datetime-local"
                    value={noteForm.scheduled_share_time}
                    onChange={(e) => setNoteForm({...noteForm, scheduled_share_time: e.target.value})}
                    placeholder="Paylaşım zamanı"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Belirtilen zamana kadar not paylaşılan kişilere görünmez
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Belge Ekleme */}
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-blue-500" />
                <Label>Belgeler</Label>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
              >
                {uploadingFile ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Belge Ekle
                  </>
                )}
              </Button>
            </div>
            
            {/* Yüklenen belgeler listesi */}
            {noteForm.attachments && noteForm.attachments.length > 0 && (
              <div className="space-y-2">
                {noteForm.attachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {getFileIcon(attachment.type)}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{attachment.filename}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openPreview(attachment)}
                        title="Ön İzleme"
                      >
                        <Eye className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(attachment.id)}
                        title="Kaldır"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              PDF, Word, Excel, resim dosyaları desteklenir. Maksimum 10MB.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>İptal</Button>
            <Button onClick={handleSaveNote} data-testid="save-note-btn">
              {editingNote ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Note Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full ${getColorClass(viewingNote?.color)}`}></div>
              {viewingNote?.title}
              {viewingNote?.is_shared && (
                <Badge variant="outline" className="ml-2">
                  <Share2 className="h-3 w-3 mr-1" />
                  Paylaşılan
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewingNote && (
            <div className="space-y-4">
              {viewingNote.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="text-gray-800 whitespace-pre-wrap">{viewingNote.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="text-gray-800">{viewingNote.date}</p>
                </div>
                {viewingNote.time && (
                  <div>
                    <Label className="text-gray-500">Saat</Label>
                    <p className="text-gray-800">{viewingNote.time}</p>
                  </div>
                )}
              </div>

              {viewingNote.alarm_enabled && viewingNote.alarm_time && (
                <div className="flex items-center gap-2 text-orange-600">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm">Alarm: {formatDateTime(viewingNote.alarm_time)}</span>
                </div>
              )}

              <div className="border-t pt-3 text-xs text-gray-500">
                <p>Oluşturan: <strong>{viewingNote.user_name}</strong></p>
                <p>Oluşturulma: {formatDateTime(viewingNote.created_at)}</p>
                {viewingNote.updated_at !== viewingNote.created_at && (
                  <p>Güncelleme: {formatDateTime(viewingNote.updated_at)}</p>
                )}
              </div>
              
              {/* Belgeler - Görüntüleme */}
              {viewingNote.attachments && viewingNote.attachments.length > 0 && (
                <div className="border-t pt-3">
                  <Label className="text-gray-500 flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4" />
                    Ekli Belgeler ({viewingNote.attachments.length})
                  </Label>
                  <div className="space-y-2">
                    {viewingNote.attachments.map((attachment) => (
                      <div 
                        key={attachment.id} 
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {getFileIcon(attachment.type)}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreview(attachment)}
                            title="Ön İzleme"
                          >
                            <Eye className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadFile(attachment)}
                            title="İndir"
                          >
                            <Download className="h-4 w-4 text-green-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {viewingNote && !viewingNote.is_shared && (
              <>
                <Button variant="destructive" size="sm" onClick={() => handleDeleteNote(viewingNote.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Sil
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  setShowViewModal(false);
                  openEditModal(viewingNote);
                }}>
                  <Edit className="h-4 w-4 mr-1" />
                  Düzenle
                </Button>
              </>
            )}
            <Button onClick={() => setShowViewModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile && getFileIcon(previewFile.type)}
              {previewFile?.filename}
            </DialogTitle>
          </DialogHeader>
          
          {previewFile && (
            <div className="flex-1 overflow-auto">
              {previewFile.type.startsWith('image/') ? (
                <img 
                  src={previewFile.url} 
                  alt={previewFile.filename}
                  className="max-w-full h-auto mx-auto"
                />
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">Bu dosya türü önizlenemiyor</p>
                  <Button onClick={() => downloadFile(previewFile)} className="gap-2">
                    <Download className="h-4 w-4" />
                    İndir
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {previewFile && (
              <Button variant="outline" onClick={() => downloadFile(previewFile)} className="gap-2">
                <Download className="h-4 w-4" />
                İndir
              </Button>
            )}
            <Button onClick={() => setShowPreviewModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Ekleme/Düzenleme Modalı */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-600" />
              {editingReminder ? 'Hatırlatıcı Düzenle' : 'Yeni Hatırlatıcı'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input
                value={reminderForm.title}
                onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})}
                placeholder="Hatırlatıcı başlığı"
                data-testid="reminder-title-input"
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={reminderForm.description}
                onChange={(e) => setReminderForm({...reminderForm, description: e.target.value})}
                placeholder="Detaylı açıklama (opsiyonel)"
                rows={3}
                data-testid="reminder-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Son Tarih *</Label>
                <Input
                  type="date"
                  value={reminderForm.due_date}
                  onChange={(e) => setReminderForm({...reminderForm, due_date: e.target.value})}
                  data-testid="reminder-due-date"
                />
              </div>
              <div>
                <Label>Öncelik</Label>
                <Select
                  value={reminderForm.priority}
                  onValueChange={(value) => setReminderForm({...reminderForm, priority: value})}
                >
                  <SelectTrigger data-testid="reminder-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>İlgili Çalışan (Opsiyonel)</Label>
              <Select
                value={reminderForm.employee_id}
                onValueChange={(value) => setReminderForm({...reminderForm, employee_id: value === "none" ? "" : value})}
              >
                <SelectTrigger data-testid="reminder-employee-select">
                  <SelectValue placeholder="Çalışan seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seçilmedi</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>İptal</Button>
            <Button 
              onClick={handleSaveReminder} 
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="save-reminder-btn"
            >
              {editingReminder ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Listesi Modalı */}
      <Dialog open={showReminderListModal} onOpenChange={setShowReminderListModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-orange-600" />
              Hatırlatıcı Listesi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Aktif Hatırlatıcılar */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-600" />
                Aktif Hatırlatıcılar
                {activeReminders.length > 0 && (
                  <Badge className="bg-orange-100 text-orange-700">{activeReminders.length}</Badge>
                )}
              </h3>
              
              {activeReminders.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Bell className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Aktif hatırlatıcı bulunmuyor</p>
                  <Button
                    onClick={() => {
                      setShowReminderListModal(false);
                      openReminderModal();
                    }}
                    className="mt-3 gap-2 bg-orange-600 hover:bg-orange-700"
                  >
                    <Plus className="h-4 w-4" />
                    Hatırlatıcı Ekle
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeReminders.map(reminder => (
                    <div 
                      key={reminder.id}
                      className={`p-3 rounded-lg border-2 ${
                        isOverdue(reminder.due_date) 
                          ? 'bg-red-50 border-red-300' 
                          : isDueToday(reminder.due_date)
                            ? 'bg-yellow-50 border-yellow-300'
                            : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {isOverdue(reminder.due_date) && (
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                            )}
                            <span className="font-semibold text-gray-900">{reminder.title}</span>
                            <Badge className={getPriorityColor(reminder.priority)}>
                              {getPriorityLabel(reminder.priority)}
                            </Badge>
                          </div>
                          {reminder.description && (
                            <p className="text-sm text-gray-600 mb-1">{reminder.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              Son Tarih: {new Date(reminder.due_date).toLocaleDateString('tr-TR')}
                            </span>
                            {reminder.employee_name && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {reminder.employee_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleCompleteReminder(reminder.id)}
                            title="Tamamla"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => {
                              setShowReminderListModal(false);
                              openReminderModal(reminder);
                            }}
                            title="Düzenle"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteReminder(reminder.id)}
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tamamlanan Hatırlatıcılar */}
            {completedReminders.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Tamamlanan Hatırlatıcılar
                  <Badge className="bg-green-100 text-green-700">{completedReminders.length}</Badge>
                </h3>
                <div className="space-y-2">
                  {completedReminders.slice(0, 5).map(reminder => (
                    <div 
                      key={reminder.id}
                      className="p-2 bg-green-50 rounded-lg border border-green-200 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-green-800 line-through">{reminder.title}</p>
                        <p className="text-xs text-green-600">
                          Tamamlandı: {reminder.completed_at ? new Date(reminder.completed_at).toLocaleDateString('tr-TR') : '-'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteReminder(reminder.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderListModal(false)}>
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setShowReminderListModal(false);
                openReminderModal();
              }}
              className="bg-orange-600 hover:bg-orange-700 gap-2"
            >
              <Plus className="h-4 w-4" />
              Yeni Hatırlatıcı
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchCalendar;
